<template>
  <div class="template-shell">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <template v-else>
      <div v-if="!loading && resources.length > 0" class="progress-track" aria-hidden="true">
        <div class="progress-fill" :style="{ transform: `scaleX(${scrollProgress})` }"></div>
      </div>

      <nav v-if="!loading && resources.length > 1" class="chapter-rail" aria-label="Navigation entre chapitres">
        <button
          v-for="(resource, index) in resources"
          :key="'dot-' + index"
          type="button"
          class="rail-dot"
          :class="{ 'is-active': activeIndex === index }"
          :aria-current="activeIndex === index ? 'true' : null"
          :aria-label="`Aller au chapitre ${index + 1}`"
          @click="scrollToChapter(index)"
        >{{ padIndex(index + 1) }}</button>
      </nav>

      <header class="masthead">
        <div class="masthead-inner">
          <p class="eyebrow">Exposition</p>
          <h1 class="masthead-title">{{ exhibitName || 'Exposition' }}</h1>
          <p v-if="cleanUserName" class="masthead-byline">Une exposition par {{ cleanUserName }}</p>
        </div>
        <div class="scroll-hint" :class="{ 'is-hidden': hasScrolled }" aria-hidden="true">
          <span>Faites défiler</span>
          <span class="scroll-hint-arrow">↓</span>
        </div>
      </header>

      <main class="story">
        <div v-if="loading" class="state-panel">Chargement de l'exposition…</div>
        <div v-else-if="resources.length === 0" class="state-panel">
          Aucune ressource dans cette exposition pour le moment.
        </div>

        <template v-else>
          <section
            v-for="(resource, index) in resources"
            :key="resource.id || resource.name || index"
            :ref="(el) => setChapterRef(el, index)"
            :data-chapter-index="index"
            class="chapter"
            :class="{ 'chapter--flip': index % 2 === 1, 'is-active': isChapterActive(index) }"
          >
            <div class="chapter-media">
              <div class="media-frame">
                <ResourceMedia
                  :resource="resource"
                  :url="resourceUrls[index]"
                  context="inline"
                  fit="cover"
                  :active="ioSupported ? activeIndex === index : null"
                  @open="openLightbox(index)"
                />
              </div>
            </div>

            <div class="chapter-text">
              <span class="chapter-number">{{ padIndex(index + 1) }}</span>
              <h2 class="chapter-title">{{ chapterTitle(resource) }}</h2>
              <p v-if="chapterDescription(resource)" class="chapter-description">{{ chapterDescription(resource) }}</p>
            </div>
          </section>
        </template>
      </main>

      <footer class="site-footer">
        <p class="footer-text">{{ exhibitName || 'Exposition' }} — Fin de l'exposition</p>
      </footer>
    </template>

    <ResourceLightbox :resources="resources" :urls="resourceUrls" v-model:selectedIndex="selectedIndex" />
  </div>
</template>

<script setup>
import { computed, defineProps, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';
import { cleanText, padIndex } from '../../utils/galleryText';
import ResourceMedia from '../../components/gallery/ResourceMedia.vue';
import ResourceLightbox from '../../components/gallery/ResourceLightbox.vue';

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  homeImage,
  homePage,
  id,
  loading,
  resourceUrls,
  resources,
  userName,
} = useGalleryTemplate(props);

/* -------------------------------- text helpers -------------------------------- */
const cleanUserName = computed(() => cleanText(userName.value));
const chapterTitle = (resource) => cleanText(resource?.title) || 'Sans titre';
const chapterDescription = (resource) => cleanText(resource?.description);

/* ---------------------------------- lightbox ---------------------------------- */
const selectedIndex = ref(null);
const openLightbox = (index) => {
  selectedIndex.value = index;
};

/* ----------------------------- active chapter tracking ------------------------ */
// Without IntersectionObserver there is no way to know which chapter the reader
// is on: disable the active-chapter effects entirely (every chapter rendered
// undimmed, no video autopilot) instead of leaving everything past chapter 1
// permanently faded.
const ioSupported = typeof IntersectionObserver !== 'undefined';
const activeIndex = ref(0);
const chapterEls = ref([]);
let chapterObserver = null;

const isChapterActive = (index) => !ioSupported || activeIndex.value === index;

const setChapterRef = (el, index) => {
  chapterEls.value[index] = el || undefined;
};

const teardownChapterObserver = () => {
  if (chapterObserver) {
    chapterObserver.disconnect();
    chapterObserver = null;
  }
};

// A zero-height "trigger line" through the vertical middle of the viewport:
// whichever chapter section currently straddles it becomes the active one.
// Works regardless of each section's actual height (~190vh, or taller with
// long descriptions), unlike a plain visibility-ratio threshold.
const setupChapterObserver = () => {
  teardownChapterObserver();
  if (!ioSupported) return;
  chapterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = Number(entry.target.dataset.chapterIndex);
        if (!Number.isNaN(index)) activeIndex.value = index;
      });
    },
    { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
  );
  chapterEls.value.forEach((el) => {
    if (el) chapterObserver.observe(el);
  });
};

watch(
  () => resources.value.length,
  async (length) => {
    chapterEls.value = [];
    activeIndex.value = 0;
    teardownChapterObserver();
    if (!length) return;
    await nextTick();
    setupChapterObserver();
  }
);

const scrollToChapter = (index) => {
  const el = chapterEls.value[index];
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

/* --------------------------- scroll progress + hint ---------------------------- */
const scrollProgress = ref(0);
const hasScrolled = ref(false);
let rafId = null;

const updateScrollProgress = () => {
  const doc = document.documentElement;
  const max = doc.scrollHeight - window.innerHeight;
  scrollProgress.value = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
  hasScrolled.value = window.scrollY > 40;
};

const onScroll = () => {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(() => {
    updateScrollProgress();
    rafId = null;
  });
};

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  updateScrollProgress();
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  if (rafId !== null) cancelAnimationFrame(rafId);
  teardownChapterObserver();
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@300;500;600&family=Inter:wght@400;500;600&display=swap');

.template-shell {
  --paper: #f8f4ea;
  --paper-deep: #efe6d2;
  --ink: #1c1912;
  --muted: #8a8171;
  --accent: #a8431f;
  --line: rgba(28, 25, 18, 0.14);
  min-height: 100vh;
  background: var(--paper);
  color: var(--ink);
  font-family: 'Inter', sans-serif;
}

/* ---------------------------------- progress ----------------------------------- */
.progress-track {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(28, 25, 18, 0.08);
  z-index: 60;
}

.progress-fill {
  height: 100%;
  width: 100%;
  transform-origin: left center;
  transform: scaleX(0);
  background: var(--accent);
}

.chapter-rail {
  position: fixed;
  right: 28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 55;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rail-dot {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--muted);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.3s ease;
}

.rail-dot:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.rail-dot.is-active {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
  transform: scale(1.12);
}

@media (max-width: 980px) {
  .chapter-rail {
    display: none;
  }
}

/* ----------------------------------- masthead ----------------------------------- */
.masthead {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  padding: 120px clamp(24px, 7vw, 120px) 80px;
  box-sizing: border-box;
}

.masthead-inner {
  max-width: 920px;
}

.eyebrow {
  margin: 0 0 20px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--accent);
}

.masthead-title {
  margin: 0 0 24px;
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: clamp(48px, 9vw, 132px);
  line-height: 0.98;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
}

.masthead-byline {
  margin: 0;
  font-size: clamp(16px, 1.6vw, 20px);
  color: var(--muted);
}

.scroll-hint {
  position: absolute;
  left: clamp(24px, 7vw, 120px);
  bottom: 56px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  transition: opacity 0.4s ease;
}

.scroll-hint.is-hidden {
  opacity: 0;
  pointer-events: none;
}

.scroll-hint-arrow {
  display: inline-block;
  font-size: 16px;
  animation: hint-bounce 1.6s ease-in-out infinite;
}

@keyframes hint-bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(7px);
  }
}

/* ------------------------------------ story ------------------------------------- */
.story {
  position: relative;
}

.state-panel {
  display: grid;
  place-items: center;
  min-height: 60vh;
  padding: 40px 24px;
  text-align: center;
  color: var(--muted);
  font-size: 18px;
}

.chapter {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: clamp(32px, 5vw, 96px);
  min-height: 190vh;
  padding: 12vh clamp(24px, 6vw, 96px);
  box-sizing: border-box;
  border-top: 1px solid var(--line);
}

.chapter:nth-child(odd) {
  background: var(--paper);
}

.chapter:nth-child(even) {
  background: var(--paper-deep);
}

.chapter--flip {
  flex-direction: row-reverse;
}

.chapter-media {
  flex: 1 1 46%;
  min-width: 0;
  align-self: flex-start;
  position: sticky;
  top: 14vh;
}

.media-frame {
  width: 100%;
  height: min(70vh, 640px);
  overflow: hidden;
  background: var(--paper-deep);
  border: 1px solid var(--line);
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.6s ease;
}

.chapter.is-active .media-frame {
  opacity: 1;
}

.chapter-text {
  flex: 1 1 42%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  opacity: 0.28;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.chapter.is-active .chapter-text {
  opacity: 1;
  transform: translateY(0);
}

.chapter-number {
  font-family: 'Fraunces', serif;
  font-weight: 300;
  font-size: clamp(26px, 3.4vw, 48px);
  color: var(--muted);
  transition: color 0.5s ease;
}

.chapter.is-active .chapter-number {
  color: var(--accent);
}

.chapter-title {
  margin: 0;
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: clamp(32px, 4.4vw, 64px);
  line-height: 1.04;
  overflow-wrap: anywhere;
}

.chapter-description {
  margin: 0;
  max-width: 46ch;
  font-size: clamp(16px, 1.3vw, 19px);
  line-height: 1.65;
  color: var(--muted);
}

/* ----------------------------------- footer -------------------------------------- */
.site-footer {
  padding: 64px 24px 72px;
  text-align: center;
  border-top: 1px solid var(--line);
  background: var(--paper);
}

.footer-text {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
}

/* --------------------------------- responsive ------------------------------------ */
@media (max-width: 980px) {
  .masthead {
    padding: 96px 28px 64px;
  }

  .chapter {
    flex-direction: column;
    gap: 28px;
    min-height: 140vh;
    padding: 9vh 24px;
  }

  .chapter--flip {
    flex-direction: column;
  }

  .chapter-media {
    top: 72px;
  }

  .media-frame {
    height: 44vh;
  }

  .chapter-text {
    opacity: 0.3;
  }
}

@media (max-width: 620px) {
  .masthead-title {
    font-size: clamp(40px, 12vw, 64px);
  }

  .chapter {
    min-height: 120vh;
    padding: 7vh 18px;
  }

  .media-frame {
    height: 38vh;
  }

  .chapter-number {
    font-size: 24px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scroll-hint-arrow {
    animation: none;
  }

  .chapter-media,
  .chapter-text,
  .media-frame,
  .chapter-number,
  .rail-dot,
  .scroll-hint {
    transition: none;
  }
}
</style>
